import { reactive, computed, watch } from 'vue';

export default function IndependentDriverSignupPersonalInfo(data = {}) {
  const state = reactive({
    firstName: data.firstName || '',
    lastName: data.lastName || '',
    email: data.email || '',
    password: data.password || '',
    confirmPassword: data.confirmPassword || '',
    countryCode: data.countryCode || '+1',
    phoneNumber: data.phoneNumber || '',
    address: data.address || '',
    placeId: data.placeId || '', //transient prop
    city: data.city || '',
    state: data.state || '',
    country: data.country || '',
    postalCode: data.postalCode || '',
    dateOfBirth: data.dateOfBirth || '',
    referralCode: data.referralCode || '',
  });

  const isIsoDate = (val) => {
    if (!val) return false;
    const m = /^\d{4}-\d{2}-\d{2}$/.exec(val);
    if (!m) return false;
    const dt = new Date(val);
    const [y, mo, d] = val.split('-').map(Number);
    return dt instanceof Date && !isNaN(dt) && dt.getUTCFullYear() === y && (dt.getUTCMonth() + 1) === mo && dt.getUTCDate() === d;
  };

  const validations = {
    isFirstNameValid: computed(() => !!String(state.firstName ?? '').trim()),
    isLastNameValid: computed(() => !!String(state.lastName ?? '').trim()),
    isEmailValid: computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(state.email ?? ''))),
    isPasswordValid: computed(() => String(state.password ?? '').length >= 8),
    isConfirmPasswordValid: computed(() => state.password === state.confirmPassword && state.confirmPassword.length > 0),
    isPhoneNumberValid: computed(() => /^[0-9]{7,15}$/.test(String(state.phoneNumber ?? ''))),
    isAddressValid: computed(() => !!String(state.address ?? '').trim()),
    isCityValid: computed(() => !!String(state.city ?? '').trim()),
    isStateValid: computed(() => !!String(state.state ?? '').trim()),
    isCountryValid: computed(() => !!String(state.country ?? '').trim()),
    isPostalCodeValid: computed(() => !!String(state.postalCode ?? '').trim()),
    isDobValid: computed(() => {
      if (!isIsoDate(String(state.dateOfBirth ?? ''))) return false;
      const dob = new Date(state.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      const dayDiff = today.getDate() - dob.getDate();
      const actualAge = (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) ? age - 1 : age;
      return actualAge >= 25 && actualAge <= 100;
    }),
  };

  const toggleInvalid = (inputSelector, errorSelector, ok) => {
    const $input = $(inputSelector);
    const $error = $(errorSelector);
    if (!$input || !$input.length) return ok;
    if (ok) {
      $input.removeClass('is-invalid');
      if ($error && $error.length) $error.addClass('d-none');
    } else {
      $input.addClass('is-invalid');
      if ($error && $error.length) $error.removeClass('d-none');
    }
    return ok;
  };

  const validateFirstName = () => toggleInvalid('#signup-first-name-input', '#signup-first-name-error', validations.isFirstNameValid.value);
  const validateLastName = () => toggleInvalid('#signup-last-name-input', '#signup-last-name-error', validations.isLastNameValid.value);
  const validateEmail = () => toggleInvalid('#signup-email-input', '#signup-email-error', validations.isEmailValid.value);
  const validatePassword = () => toggleInvalid('#signup-password-input', '#signup-password-error', validations.isPasswordValid.value);
  const validateConfirmPassword = () => toggleInvalid('#signup-confirm-password-input', '#signup-confirm-password-error', validations.isConfirmPasswordValid.value);
  const validateDob = () => toggleInvalid('#signup-dob-input', '#signup-dob-error', validations.isDobValid.value);
  const validatePhone = () => toggleInvalid('#signup-phone-number-input', '#signup-phone-number-error', validations.isPhoneNumberValid.value);
  const validateAddress = () => toggleInvalid('#signup-address-input', '#signup-address-error', validations.isAddressValid.value);
  const validateCity = () => toggleInvalid('#signup-city-input', '#signup-city-error', validations.isCityValid.value);
  const validateState = () => toggleInvalid('#signup-state-input', '#signup-state-error', validations.isStateValid.value);
  const validateCountry = () => toggleInvalid('#signup-country-input', '#signup-country-error', validations.isCountryValid.value);
  const validatePostalCode = () => toggleInvalid('#signup-postal-code-input', '#signup-postal-code-error', validations.isPostalCodeValid.value);

  watch(() => state.firstName, validateFirstName);
  watch(() => state.lastName, validateLastName);
  watch(() => state.email, validateEmail);
  watch(() => state.password, validatePassword);
  watch(() => state.confirmPassword, validateConfirmPassword);
  watch(() => state.dateOfBirth, validateDob);
  watch(() => state.phoneNumber, validatePhone);
  watch(() => state.address, validateAddress);
  watch(() => state.city, validateCity);
  watch(() => state.state, validateState);
  watch(() => state.country, validateCountry);
  watch(() => state.postalCode, validatePostalCode);

  const isValid = () => (
    validateFirstName() |
    validateLastName() |
    validateEmail() |
    validatePassword() |
    validateConfirmPassword() |
    validatePhone() |
    validateAddress() |
    validateCity() |
    validateState() |
    validateCountry() |
    validatePostalCode() |
    validateDob()
  );

  const toJSON = () => {
    const { confirmPassword, countryCode, phoneNumber, ...rest } = state;
    return {
      ...rest,
      phoneNumber: `${countryCode}${phoneNumber}`
    };
  };

  return { state, validations, isValid, toJSON };
}
